import { z } from 'zod';

/**
 * Environment is validated once at boot. CLAUDE.md #9: no hard-coded config,
 * and a missing key must fail loudly rather than half-boot the app.
 */
const envSchema = z.object({
  apiKey: z.string().min(1, 'VITE_FIREBASE_API_KEY is missing'),
  authDomain: z.string().min(1),
  projectId: z.string().min(1),
  storageBucket: z.string().min(1),
  messagingSenderId: z.string().min(1),
  appId: z.string().min(1),
  measurementId: z.string().optional(),
  recaptchaSiteKey: z.string().optional(),
  appCheckDebugToken: z.string().optional(),
  vapidKey: z.string().optional(),
  useEmulators: z.boolean(),
  /** Deliberate pre-launch escape hatch; see firebase.ts. */
  allowNoAppCheck: z.boolean(),
  appVersion: z.string(),
});

export type AppEnv = z.infer<typeof envSchema>;

let cached: AppEnv | null = null;

export function readEnv(): AppEnv {
  if (cached) return cached;
  const parsed = envSchema.safeParse({
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
    measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
    recaptchaSiteKey: import.meta.env.VITE_RECAPTCHA_ENTERPRISE_SITE_KEY,
    appCheckDebugToken: import.meta.env.VITE_APPCHECK_DEBUG_TOKEN,
    vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY,
    useEmulators: import.meta.env.VITE_USE_EMULATORS === '1',
    allowNoAppCheck: import.meta.env.VITE_ALLOW_NO_APP_CHECK === '1',
    appVersion: import.meta.env.VITE_APP_VERSION ?? '0.1.0-dev',
  });

  if (!parsed.success) {
    const missing = parsed.error.issues.map((i) => i.path.join('.')).join(', ');
    throw new Error(
      `Firebase env vars missing: ${missing}. Copy .env.example to apps/web/.env.local and fill it in.`,
    );
  }
  cached = parsed.data;
  return cached;
}

export const isDev = import.meta.env.DEV;
